import { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { useBranding } from "../contexts/BrandingContext";
import { useTranslation } from "react-i18next";
import { useBusinessLabels } from "../hooks/useBusinessLabels";
import type { BusinessType } from "../types";
import { resolveHomeSubtitle } from "../utils/resolveHomeSubtitle";
import { PLATFORM_BRAND } from "../config/platformBrand";
import LoadingScreen from "../components/ui/LoadingScreen";
import IosInstallBanner from "../components/ui/IosInstallBanner";
import PoweredBy from "../components/ui/PoweredBy";
import YulqoFace from "../components/ui/YulqoFace";
import EventCodeInput from "../components/ui/EventCodeInput";
import styles from "./HomePage.module.css";
import { mapEventError, type ApiErrorBody } from "../utils/mapEventError";
import { consumeSkipQueueRestore } from "../utils/queueSession";

type ErrorType = "error" | "warning" | "info";

const HomePage: React.FC = () => {
  const { branding, isLoading: brandingLoading } = useBranding();
  const { organization } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [checkingClient, setCheckingClient] = useState(true);
  const [eventCode, setEventCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorType, setErrorType] = useState<ErrorType>("error");
  const [iosBanner, setIosBanner] = useState<null | "notSafari" | "safari">(null);
  const labels = useBusinessLabels("OTHER");

  const mapEventErrorLocal = useCallback(
    (body?: { code?: string; details?: { eventName?: string; startDate?: string } }): { message: string; type: ErrorType } =>
      mapEventError(body, t, i18n.language),
    [t, i18n.language],
  );

  useEffect(() => {
    const ua = navigator.userAgent;
    const isIOS = /iphone|ipad|ipod/i.test(ua);
    if (!isIOS) return;

    const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
    if (isStandalone) return;

    const isSafari =
      /safari/i.test(ua) && !/crios|fxios|opios|mercury|chrome/i.test(ua);

    setIosBanner(isSafari ? "safari" : "notSafari");
  }, []);

  useEffect(() => {
    const checkExistingClient = async () => {
      if (consumeSkipQueueRestore()) {
        setCheckingClient(false);
        return;
      }

      const clientIds: string[] = [];
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith("clientId_")) {
          const clientId = localStorage.getItem(key);
          if (clientId) clientIds.push(clientId);
        }
      });

      if (clientIds.length === 0) {
        setCheckingClient(false);
        return;
      }

      try {
        const results = await Promise.allSettled(
          clientIds.map((clientId) =>
            api.getClientPosition(clientId).then((data) => ({ clientId, data })),
          ),
        );

        for (const result of results) {
          if (result.status === "fulfilled") {
            const { clientId, data } = result.value;
            if (data && data.status !== "COMPLETED" && data.status !== "CANCELLED") {
              navigate(`/status?clientId=${clientId}`, { replace: true });
              return;
            }
            Object.keys(localStorage).forEach((key) => {
              if (
                key.startsWith("clientId_") &&
                localStorage.getItem(key) === clientId
              ) {
                localStorage.removeItem(key);
              }
            });
          } else {
            Object.keys(localStorage).forEach((key) => {
              if (key.startsWith("clientId_")) localStorage.removeItem(key);
            });
          }
        }
      } catch {
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith("clientId_")) localStorage.removeItem(key);
        });
      }

      setCheckingClient(false);
    };

    checkExistingClient();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setErrorType("error");

    try {
      const event = await api.verifyEventCode(eventCode.toUpperCase());
      const org = event.organization;
      const businessType = (org?.businessType as BusinessType) || "OTHER";
      sessionStorage.setItem(`businessType_${event.id}`, businessType);
      if (event.organizationId) {
        sessionStorage.setItem(`queueOrgId_${event.id}`, event.organizationId);
      }
      navigate(`/join?eventId=${event.id}`);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: ApiErrorBody } };
      const mapped = mapEventErrorLocal(axiosErr.response?.data);
      setErrorType(mapped.type);
      setError(mapped.message);
    } finally {
      setLoading(false);
    }
  };

  if (checkingClient || brandingLoading) {
    return <LoadingScreen message={t("home.checking")} />;
  }

  const storedSubtitle = branding.homeSubtitle;
  const isPlatformTagline =
    !storedSubtitle ||
    storedSubtitle === t("brand.tagline") ||
    storedSubtitle === PLATFORM_BRAND.tagline;
  const subtitle = isPlatformTagline
    ? t("look.lead")
    : resolveHomeSubtitle(
        storedSubtitle,
        organization?.businessType ?? "OTHER",
        organization?.name || branding.homeTitle,
        t,
        t("look.lead"),
      );

  return (
    <div className={styles.page}>
      {iosBanner && (
        <IosInstallBanner variant={iosBanner} onDismiss={() => setIosBanner(null)} />
      )}

      <div className={styles.wrap}>
        <div className={styles.join}>
          <section className={styles.hero}>
            <div className={styles.sun} aria-hidden="true" />
            <span className={styles.hand}>{t("look.hello")}</span>
            <h1 className={styles.mega}>
              {t("look.megaLine1")}
              <br />
              {t("look.megaSolo")}{" "}
              <em className={styles.sq}>
                {t("look.megaEm")}
                <svg viewBox="0 0 200 20" preserveAspectRatio="none" aria-hidden="true">
                  <path
                    d="M4 14 C 50 4, 120 4, 196 12"
                    stroke="#F4C95D"
                    strokeWidth="7"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </em>
            </h1>
            <p className={styles.lead}>{subtitle}</p>
            <div className={styles.sticker} aria-hidden="true">
              <small>{t("look.yourNumber")}</small>
              <b>47</b>
            </div>
            <div className={styles.scene} aria-hidden="true">
              {[40, 46, 52, 58, 78].map((size, i) => (
                <span key={size} className={styles.bobWrap} style={{ animationDelay: `${i * 0.25}s` }}>
                  <YulqoFace size={size} index={i} me={i === 4} bob />
                </span>
              ))}
              <svg className={styles.mug} width="86" height="86" viewBox="0 0 90 90" fill="none">
                <path d="M20 40h44v20a16 16 0 0 1-16 16H36a16 16 0 0 1-16-16z" fill="#FBF4EA" stroke="#3B2A20" strokeWidth="3" />
                <path d="M64 46h6a8 8 0 0 1 0 16h-6" stroke="#3B2A20" strokeWidth="3" />
                <g className={styles.steam}>
                  <path d="M32 30c-4-6 4-8 0-14" stroke="#B85E35" strokeWidth="3" strokeLinecap="round" />
                  <path d="M42 30c-4-6 4-8 0-14" stroke="#B85E35" strokeWidth="3" strokeLinecap="round" />
                  <path d="M52 30c-4-6 4-8 0-14" stroke="#B85E35" strokeWidth="3" strokeLinecap="round" />
                </g>
              </svg>
            </div>
          </section>

          <section className={styles.card}>
            <span className={styles.step}>{t("look.step1")}</span>
            <div>
              <h2 id="code-title">{t("look.codeTitle")}</h2>
              <p className={styles.sub}>{t("look.codeHelp")}</p>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              <EventCodeInput
                value={eventCode}
                onChange={setEventCode}
                labelledBy="code-title"
              />
              <p className={styles.hint}>{labels.eventCodeHint}</p>

              {error && (
                <div
                  className={
                    errorType === "info"
                      ? styles.alertInfo
                      : errorType === "warning"
                        ? styles.alertWarning
                        : styles.alertError
                  }
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || eventCode.length < 4}
                className={styles.submitBtn}
              >
                {loading ? t("home.searching") : t("look.takeTurn")}
              </button>
            </form>

            <div className={styles.or}>{t("look.or")}</div>

            <button
              type="button"
              onClick={() => navigate("/admin/login")}
              className={styles.adminBtn}
            >
              {t("home.artistAccess")}
            </button>

            <div className={styles.trust}>
              <span><i />{t("look.noApp")}</span>
              <span><i />{t("look.noSignup")}</span>
              <span><i />{t("look.phonePing")}</span>
            </div>
          </section>
        </div>
      </div>

      <div className={styles.footer}>
        <PoweredBy />
      </div>
    </div>
  );
};

export default HomePage;
